// @ts-nocheck
import React from 'react';
import PropTypes from 'prop-types';
import { Card, Box, Typography } from '@mui/material';
import { ReactComponent as AutoAwesomeIcon } from '../../../assets/Icons/lucideIcons/sparkles.svg';
import { useTheme } from '@mui/material/styles';

const formatTooltipValue = (value) => {
  if (value === null || value === undefined) {
    return '(empty)';
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : '(empty)';
  }
  if (typeof value === 'object') {
    return value?.title || JSON.stringify(value);
  }
  return String(value);
};

const getConfidenceDotColor = (confidence, theme) => {
  if (confidence >= 0.9) {
    return theme?.palette?.success?.main;
  }
  if (confidence >= 0.75) {
    return theme?.palette?.info?.main;
  }
  return theme?.palette?.warning?.main;
};

const getConfidenceText = (confidence) => {
  if (confidence >= 0.9) {
    return 'High confidence';
  }
  if (confidence >= 0.75) {
    return 'Medium confidence';
  }
  return 'Low confidence';
};

const BadgePopoverContent = React.memo(
  ({ suggestion, isPending, isAccepted }) => {
    const theme = useTheme();

    return (
      <Card elevation={0}>
        <Box
          sx={{
            px: 1.5,
            pt: 1.25,
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${theme?.palette?.other?.borderColor}`,
            backgroundColor: isPending
              ? theme?.palette?.other?.aiHighlightBackgroundBase
              : theme?.palette?.other?.successBackgroundColor,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            <AutoAwesomeIcon
              sx={{
                fontSize: 13,
                color: isPending
                  ? theme?.palette?.other?.aiPrimary
                  : theme?.palette?.success?.main,
              }}
            />
            <Typography
              variant="captionBold"
              sx={{
                color: isPending
                  ? theme?.palette?.other?.aiPrimary
                  : theme?.palette?.success?.main,
              }}
            >
              {isPending ? 'AI Suggestion' : 'Accepted'}
            </Typography>
          </Box>
          {suggestion?.confidence != null && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: getConfidenceDotColor(
                    suggestion?.confidence,
                    theme
                  ),
                }}
              />
              <Typography
                variant="smallCaption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {getConfidenceText(suggestion?.confidence)}
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ px: 1.5, py: 1 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 0.75,
              mb: 0.5,
              alignItems: 'flex-start',
            }}
          >
            <Typography
              variant="smallCaption"
              sx={{
                color: 'text.secondary',
                minWidth: 30,
                flexShrink: 0,
              }}
            >
              Old
            </Typography>
            <Typography
              variant="caption"
              sx={{
                textDecoration:
                  isPending || isAccepted ? 'line-through' : 'none',
                color: 'text.secondary',
                wordBreak: 'break-word',
              }}
            >
              {formatTooltipValue(suggestion?.oldValue)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 0.75,
              alignItems: 'flex-start',
            }}
          >
            <Typography
              variant="smallCaption"
              sx={{
                minWidth: 30,
                flexShrink: 0,
                color: isPending
                  ? theme?.palette?.other?.aiPrimary
                  : theme?.palette?.success?.main,
              }}
            >
              New
            </Typography>
            <Typography
              variant="captionSemibold"
              sx={{
                color: isPending
                  ? theme?.palette?.other?.aiPrimary
                  : theme?.palette?.success?.main,
                wordBreak: 'break-word',
              }}
            >
              {formatTooltipValue(suggestion?.newValue)}
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  }
);

BadgePopoverContent.displayName = 'BadgePopoverContent';

BadgePopoverContent.propTypes = {
  suggestion: PropTypes.shape({
    oldValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.array,
      PropTypes.object,
    ]),
    newValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.array,
      PropTypes.object,
    ]),
    confidence: PropTypes.number,
  }),
  isPending: PropTypes.bool.isRequired,
  isAccepted: PropTypes.bool.isRequired,
};

export default BadgePopoverContent;
